import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Minus, Plus, RefreshCw, SearchX } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterChips } from '@/components/ui/filter-chips';
import { SearchInput } from '@/components/ui/search-input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatCurrency } from '@/lib/utils';
import { addonLabel } from '@/lib/catalog-labels';
import {
  ADDON_GROUP_ORDER, EMPTY_FILTER, addonGroup, addonStatus, filterAddons, groupAddons, isFilterActive,
  limitEffects, limitFromTo, type AddonFilter, type AddonGroupKey, type BasketAddon, type StatusFilter,
} from '@/lib/addon-basket';
import { PassThroughNote } from './PassThroughNote';

/** Debounce a value (the server price preview must not fire on every stepper click). */
export function useDebouncedValue<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

export interface AddonPickerProps {
  addons: BasketAddon[];
  /** What the stepper / checkbox shows, by add-on id. */
  draft: Record<string, number>;
  /** What the business has now, by add-on id. */
  saved: Record<string, number>;
  /**
   * 'replace' (platform): draft is the whole selection, saved is its starting point.
   * 'add' (tenant request): draft holds only what is being requested, on top of saved.
   */
  mode: 'replace' | 'add';
  /** Add-ons the plan already includes (cannot be toggled). */
  includedIds?: ReadonlySet<string>;
  /** Current server limits (maxUsers ...) for the "limit X -> Y" words. */
  currentLimits?: Record<string, number | null | undefined> | null;
  currency?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  onChange: (addonId: string, quantity: number) => void;
  /** Content above the filters in the left rail (the plan selector on the platform screen). */
  railTop?: ReactNode;
  /** The sticky right-hand summary. */
  summary: ReactNode;
  /** Banner(s) above the cards. */
  notices?: ReactNode;
}

const STATUS_FILTERS: StatusFilter[] = ['all', 'active', 'available'];

function unitWord(t: (k: string, o?: Record<string, unknown>) => string, addon: BasketAddon): string {
  return addon.limitType ? t(`addonUnit_${addon.limitType}`, { defaultValue: t('addonUnit_default') }) : t('addonUnit_default');
}

interface CardProps {
  addon: BasketAddon;
  mode: 'replace' | 'add';
  draftQty: number;
  savedQty: number;
  included: boolean;
  currentLimits?: Record<string, number | null | undefined> | null;
  currency?: string;
  disabled?: boolean;
  onChange: (qty: number) => void;
}

function AddonCard({ addon, mode, draftQty, savedQty, included, currentLimits, currency, disabled, onChange }: CardProps) {
  const { t } = useTranslation('settings');
  const label = addonLabel(addon.key, addon.name);
  const isPack = addon.type === 'QUANTITY';
  const price = formatCurrency(Number(addon.priceMonthly ?? 0), currency);
  const ownedModule = mode === 'add' && !isPack && savedQty > 0;
  const locked = included || ownedModule;
  const status = addonStatus(mode === 'replace' ? draftQty : savedQty, included);
  const selected = !locked && draftQty > 0;
  const baseline = mode === 'replace' ? savedQty : 0;
  const delta = draftQty - baseline;
  const effects = limitEffects(addon);

  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-2 rounded-xl border p-3.5 transition-colors',
        selected ? 'border-primary/40 bg-primary/10' : 'bg-card',
        locked && 'bg-muted/30',
      )}
      data-testid={`addon-card-${addon.key}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground" title={label}>{label}</p>
          <p className="text-xs font-medium text-primary-text">
            <bdi dir="ltr">
              {isPack
                ? t('addonPricePerUnit', { price, unit: unitWord(t, addon) })
                : t('addonPriceModule', { price })}
            </bdi>
          </p>
        </div>
        <Badge variant={status === 'included' ? 'info' : status === 'active' ? 'success' : 'outline'} className="shrink-0">
          {status === 'included' ? t('addonStatusIncluded') : status === 'active' ? t('addonStatusActiveChip') : t('addonStatusAvailableChip')}
        </Badge>
      </div>

      {addon.description && (
        <p className="line-clamp-2 text-xs text-muted-foreground" title={addon.description}>{addon.description}</p>
      )}

      {effects.length > 0 && (
        <ul className="space-y-0.5 text-xs text-foreground">
          {effects.map((fx) => {
            const ft = limitFromTo(currentLimits?.[fx.limitKey], fx, baseline, draftQty);
            return (
              <li key={fx.limitKey} className="flex flex-wrap items-baseline gap-x-2">
                <span>{t('addonEffectLine', { label: t(`addonLimitName_${fx.limitKey}`), n: fx.perUnit, unit: unitWord(t, addon) })}</span>
                {ft && delta !== 0 && (
                  <span className="font-medium text-primary-text">
                    <bdi dir="ltr">
                      {t('addonLimitFromTo', {
                        from: ft.from === null ? t('addonNoLimit') : ft.from,
                        to: ft.to === null ? t('addonNoLimit') : ft.to,
                      })}
                    </bdi>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {addon.limitType === 'max_mobile_links' && (
        <p className="text-xs text-muted-foreground">{t('addonMobileHelp')}</p>
      )}
      {addon.isPassThrough && <PassThroughNote providerName={addon.providerName} />}

      <div className="mt-auto pt-1">
        {locked ? (
          <p className="text-xs text-muted-foreground">{included ? t('addonIncludedWhy') : t('addonOwnedWhy')}</p>
        ) : isPack ? (
          <div className="flex items-center gap-2" role="group" aria-label={label}>
            <Button
              type="button"
              variant="outline"
              className="h-11 w-11 p-0"
              disabled={disabled || draftQty <= 0}
              aria-label={t('addonQtyDec', { name: label })}
              onClick={() => onChange(Math.max(0, draftQty - 1))}
            >
              <Minus />
            </Button>
            <span className="min-w-[2.5rem] text-center text-base font-semibold" aria-live="polite"><bdi dir="ltr">{draftQty}</bdi></span>
            <Button
              type="button"
              variant="outline"
              className="h-11 w-11 p-0"
              disabled={disabled}
              aria-label={t('addonQtyInc', { name: label })}
              onClick={() => onChange(draftQty + 1)}
            >
              <Plus />
            </Button>
            {mode === 'add' && savedQty > 0 && (
              <span className="text-xs text-muted-foreground"><bdi dir="ltr">{t('addonOwnedQty', { n: savedQty })}</bdi></span>
            )}
          </div>
        ) : (
          <label className="flex min-h-[44px] cursor-pointer items-center gap-2 text-sm font-normal text-foreground">
            <Checkbox
              checked={draftQty > 0}
              disabled={disabled}
              onChange={(e) => onChange(e.target.checked ? 1 : 0)}
              aria-label={label}
            />
            {draftQty > 0 ? t('addonSelected') : t('addonSelect')}
          </label>
        )}
      </div>
    </div>
  );
}

export function AddonPicker(props: AddonPickerProps) {
  const { t } = useTranslation('settings');
  const { addons, draft, saved, mode, includedIds, currentLimits, currency, disabled, loading, error, onRetry, onChange, railTop, summary, notices } = props;
  const [filter, setFilter] = useState<AddonFilter>(EMPTY_FILTER);

  // "Active" in the filter = in the screen's selection: the whole draft (replace) or what the business owns (add).
  const activeQty = mode === 'replace' ? draft : saved;
  const visible = useMemo(
    () => filterAddons(addons, filter, { qtyById: activeQty, labelOf: (a) => addonLabel(a.key, a.name) }),
    [addons, filter, activeQty],
  );
  const sections = useMemo(() => groupAddons(visible), [visible]);
  const presentGroups = useMemo(() => new Set(addons.map((a) => addonGroup(a))), [addons]);
  const counts = useMemo(() => {
    const m = new Map<AddonGroupKey, number>();
    for (const a of addons) m.set(addonGroup(a), (m.get(addonGroup(a)) ?? 0) + 1);
    return m;
  }, [addons]);

  const groupChips = [
    { key: 'all', label: t('addonGroupAll'), count: addons.length },
    ...ADDON_GROUP_ORDER.filter((g) => presentGroups.has(g)).map((g) => ({ key: g, label: t(`addonGroup_${g}`), count: counts.get(g) ?? 0 })),
  ];
  const statusChips = STATUS_FILTERS.map((s) => ({
    key: s,
    label: t(s === 'all' ? 'addonStatusAll' : s === 'active' ? 'addonStatusActive' : 'addonStatusAvailable'),
  }));

  return (
    <div className="grid gap-4 lg:grid-cols-[13rem_minmax(0,1fr)] xl:grid-cols-[13rem_minmax(0,1fr)_19rem]">
      {/* Zone 1: filters */}
      <aside className="space-y-4 lg:sticky lg:top-0 lg:max-h-[calc(94dvh-10rem)] lg:self-start lg:overflow-y-auto" aria-label={t('addonFiltersTitle')}>
        {railTop}
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('addonFiltersTitle')}</p>
          {/* Enter in the search box must not submit the surrounding form. */}
          <div onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>
            <SearchInput
              value={filter.search}
              onChange={(search) => setFilter((f) => ({ ...f, search }))}
              placeholder={t('addonSearchPlaceholder')}
              debounceMs={150}
            />
          </div>
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">{t('addonGroupsLabel')}</p>
            <FilterChips
              chips={groupChips}
              value={filter.group}
              onChange={(g) => setFilter((f) => ({ ...f, group: g as AddonFilter['group'] }))}
              size="sm"
              wrap
            />
          </div>
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">{t('addonStatusLabel')}</p>
            <FilterChips
              chips={statusChips}
              value={filter.status}
              onChange={(s) => setFilter((f) => ({ ...f, status: s as StatusFilter }))}
              size="sm"
              wrap
            />
          </div>
          {isFilterActive(filter) && (
            <Button type="button" variant="link" className="h-auto p-0 text-xs" onClick={() => setFilter(EMPTY_FILTER)}>
              {t('addonClearFilters')}
            </Button>
          )}
          {!loading && !error && (
            <p className="text-xs text-muted-foreground" aria-live="polite">
              {t('addonShowingCount', { shown: visible.length, total: addons.length })}
            </p>
          )}
        </div>
      </aside>

      {/* Zone 2: grouped cards */}
      <section className="min-w-0 space-y-5" aria-label={t('addonCardsTitle')}>
        {notices}
        {loading ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(17rem,1fr))] gap-3" aria-busy="true">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-36 rounded-xl" />)}
          </div>
        ) : error ? (
          <EmptyState
            icon={AlertTriangle}
            title={t('addonLoadError')}
            action={<Button type="button" variant="outline" onClick={onRetry}><RefreshCw />{t('addonRetry')}</Button>}
          />
        ) : addons.length === 0 ? (
          <EmptyState title={t('addonNone')} />
        ) : sections.length === 0 ? (
          <EmptyState
            icon={SearchX}
            title={t('addonNoMatch')}
            action={<Button type="button" variant="outline" onClick={() => setFilter(EMPTY_FILTER)}>{t('addonClearFilters')}</Button>}
          />
        ) : (
          sections.map((section) => (
            <div key={section.group} className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">
                {t(`addonGroup_${section.group}`)}
                <span className="ms-2 text-xs font-normal text-muted-foreground"><bdi dir="ltr">{section.items.length}</bdi></span>
              </h3>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(17rem,1fr))] gap-3">
                {section.items.map((addon) => (
                  <AddonCard
                    key={addon.id}
                    addon={addon}
                    mode={mode}
                    draftQty={draft[addon.id] ?? 0}
                    savedQty={saved[addon.id] ?? 0}
                    included={includedIds?.has(addon.id) ?? false}
                    currentLimits={currentLimits}
                    currency={currency}
                    disabled={disabled}
                    onChange={(q) => onChange(addon.id, q)}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </section>

      {/* Zone 3: summary */}
      <aside className="lg:col-span-2 xl:col-span-1 xl:sticky xl:top-0 xl:max-h-[calc(94dvh-10rem)] xl:self-start xl:overflow-y-auto" aria-label={t('addonSummaryTitle')}>
        {summary}
      </aside>
    </div>
  );
}
